/* eslint-disable react/prop-types */

import React, { useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, Linking } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions } from '@react-navigation/native';
import { PDFViewer, pdf as renderPdf, Font } from '@react-pdf/renderer';
import axios from 'axios';

import { Loader } from '@components/screen/Layout';
import { isWebPlatform, isWeb, isMobileWeb } from '@global/screens';
import PdfCustomer from './components/PdfCustomer.web';
import PdfSkpExternal from './components/PdfSkpExternal.web';
import PdfOrder from './components/PdfOrder.web';
import PdfCatalog from './components/PdfCatalog.web';
import PdfRequestProduct from './components/PdfRequestProduct.web';
import PdfInvoiceCollection from './components/PdfInvoiceCollection.web';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';

import Colors from '@global/colors';
import { getEnvironment, currency, imageSource, isVn } from '@global/helpers';
import { getCustomerId } from '@api/ms-account';
import { MsOrder } from '@api/ms-order';
import { MsCatalog } from '@api/ms-catalog';
import { MsB2b } from '@api/ms-b2b';
import { BRAND_LIMIT, PRODUCT_LIMIT } from './components/constant';
import * as Auth from '@/store/auth/actions';

const Config = getEnvironment();

const AVAILABLE_TYPE = [
  'customer',
  'order',
  'skp',
  'catalog',
  'request-product',
  'invoice-collection',
];

const TEMPLATE_MAP = {
  customer: PdfCustomer,
  order: PdfOrder,
  skp: PdfSkpExternal,
  catalog: PdfCatalog,
  'request-product': PdfRequestProduct,
  'invoice-collection': PdfInvoiceCollection,
};

const getFilename = (type, id = null) => {
  if (type == 'customer') {
    return `customer_${id}_${Date.now()}.pdf`;
  } else if (type == 'order') {
    return `order_${id}_${Date.now()}.pdf`;
  } else if (type == 'skp') {
    return `skp_${id}_${Date.now()}.pdf`;
  } else if (type == 'catalog') {
    return `catalog_${Date.now()}.pdf`;
  } else if (type == 'request-product') {
    return `request_product_${id}_${Date.now()}.pdf`;
  } else if (type == 'invoice-collection') {
    return `invoice_collection_${Date.now()}.pdf`;
  } else {
    return null;
  }
};

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWubEbWmTggvWl0Qn.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuaabWmTggvWl0Qn.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWub2bWmTggvWl0Qn.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://fonts.gstatic.com/s/roboto/v49/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuYjammTggvWl0Qn.ttf',
      fontWeight: 700,
    },
    {
      src: 'https://fonts.gstatic.com/s/roboto/v49/KFOKCnqEu92Fr1Mu53ZEC9_Vu3r1gIhOszmOClHrs6ljXfMMLoHQiA_0klQnx24.ttf',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: 'https://fonts.gstatic.com/s/roboto/v49/KFOKCnqEu92Fr1Mu53ZEC9_Vu3r1gIhOszmOClHrs6ljXfMMLmbXiA_0klQnx24.ttf',
      fontWeight: 700,
      fontStyle: 'italic',
    },
  ],
});

const PdfViewerScreen = (props) => {
  const dispatch = useDispatch();
  const isLoggedin = useSelector((state) => state.auth.isLoggedIn);
  const [mounted, setMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState();
  const [blob, setBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState();

  const params = props?.route?.params;
  const pdfType = params?.type;
  const id = params?.id;
  const Template = TEMPLATE_MAP[pdfType];
  const showViewer = !!Template && !isLoading && !!data && !!blob;

  const docElement = useMemo(() => {
    if (!Template) {
      return null;
    }
    return <Template data={data} />;
  }, [Template, data]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const fetchCustomerData = async () => {
    const res = await getCustomerId(id);
    const result = res?.data?.data;
    if (result) {
      setData(result);
    }
  };

  const fetchSkpData = async () => {
    const res = await MsOrder.getSkpExternalDetail(id);
    const result = res?.data?.data;

    if (result) {
      const cus = await getCustomerId(result.customer.id);
      if (cus?.data?.data) {
        result.customer = cus.data.data;
      }

      const skpInternal = await MsOrder.getSkpInternalDetail(
        result.skp_internal.id,
      );
      if (skpInternal?.data?.data) {
        result.skp_internal = skpInternal.data.data;
      }

      const coa = await MsOrder.getCoaTaxSettingsDetail(
        result.skp_internal?.coa_tax?.id,
      );
      if (coa?.data?.data) {
        const coaData = coa.data.data;

        const taxCorporate = (coaData.tax_corporate || []).find(
          (t) => t.is_default,
        );
        if (taxCorporate) {
          coaData.pph_npwp_badan = taxCorporate.tax_value;
        }
        const taxPersonal = (coaData.tax_personal || []).find(
          (t) => t.is_default,
        );
        if (taxPersonal) {
          coaData.pph_npwp_pribadi = taxPersonal.tax_value;
        }
        const nonTaxCorporate = (coaData.non_tax_corporate || []).find(
          (t) => t.is_default,
        );
        if (nonTaxCorporate) {
          coaData.pph_non_npwp_badan = nonTaxCorporate.tax_value;
        }
        const nonTaxPersonal = (coaData.non_tax_personal || []).find(
          (t) => t.is_default,
        );
        if (nonTaxPersonal) {
          coaData.pph_non_npwp_pribadi = nonTaxPersonal.tax_value;
        }

        result.skp_internal.coa_tax = coaData;

        result.wht = result.pph_percentage || 0;
      }

      const brands = await MsOrder.getAllSkpExternalsBrandBudget({
        params: {
          filter: {
            'skp_external.id': id,
            'customer.id': result.customer.id,
          },
        },
      });
      if (brands?.data?.data) {
        let total_budget_dpp = 0;
        result.brands = [];

        for (const budget of brands.data.data) {
          total_budget_dpp += Number(budget.dpp);
          result.brands.push({
            name: budget.brand?.name,
            dpp: currency(Number(budget.dpp), true),
          });
        }
        result.brands.push({
          name: 'Total',
          dpp: currency(total_budget_dpp, true),
        });
      }

      result.skp_type =
        result?.skp_internal?.skp_type?.toLowerCase() ||
        result?.skp_internal?.market_type?.code?.toLowerCase();
      setData(result);
    }
  };

  const fetchOrderData = async () => {
    const res = await MsOrder.getSalesOrderDetail(id);
    if (res?.data?.data) {
      const result = res.data.data;

      const cus = await getCustomerId(result.customer.id);
      if (cus?.data?.data) {
        result.customer = cus.data.data;
      }
      if (result?.user?.team_id) {
        const st = await MsB2b.getSalesTeamDetail(result.user.team_id);
        if (st?.data?.data) {
          const stData = st.data.data;
          result.sales_team = {
            payment_method: stData?.payment_method,
          };
        }
      }

      const products = await MsOrder.getAllProductSalesOrder(id);
      if (products?.data?.data) {
        const prods = products.data.data;
        const formattedProducts = [];

        for (let i = 0; i < prods.length; i++) {
          const pr = prods[i];

          const discount1 = pr?.discount_percentage1?.prorate_percentage || 0;
          const discount2 = pr?.discount_percentage2?.prorate_percentage || 0;

          if (pr.product_type == 'gwp_levelling_free_product') {
            formattedProducts.push({
              name: `[${pr?.combination?.reference}] ${pr?.name}`,
              quantity: `${pr?.quantity || 0} Units`,
              unit_price: `${currency(pr?.unit_price || 0, false)}.00`,
              discount_1: '0.00',
              discount_2: '0.00',
              taxes: '-',
              amount: currency(0, true),
            });
          } else {
            formattedProducts.push({
              name: `[${pr?.combination?.reference}] ${pr?.name}`,
              quantity: `${pr?.quantity || 0} Units`,
              unit_price: `${currency(pr?.unit_price || 0, false)}.00`,
              discount_1: `${discount1}.00`,
              discount_2: `${discount2}.00`,
              taxes: `${pr?.taxes?.type == 'inc' ? 'Inc' : 'Exc'} VAT`,
              amount: currency(pr?.total_price_before_tax, true),
            });
          }

          if (pr.free_product?.id) {
            formattedProducts.push({
              name: `[${pr?.free_product?.sku}] ${pr?.free_product?.name}`,
              quantity: `${pr?.free_product?.quantity || 0} Units`,
              unit_price: `${currency(pr?.free_product?.price || 0, false)}.00`,
              discount_1: '0.00',
              discount_2: '0.00',
              taxes: '-',
              amount: currency(0, true),
            });
          }
        }

        result.products = formattedProducts;
      }

      setData(result);
    }
  };

  const fetchRequestProductData = async () => {
    const PAGE_SIZE = 20;
    const detailRes = await MsOrder.getRequestProductTesterDetail(id);
    const detail = detailRes?.data?.data;
    if (!detail) {
      return;
    }

    const products = [];
    let skip = 0;
    let hasMore = true;
    while (hasMore) {
      const res = await MsOrder.getProductsFromRequestProductTester(id, {
        params: { filter: { is_deleted: false }, limit: PAGE_SIZE, skip },
      });
      const batch = res?.data?.data || [];
      products.push(...batch);
      hasMore = batch.length === PAGE_SIZE;
      skip += PAGE_SIZE;
    }

    setData({ detail, products });
  };

  const fetchInvoiceCollectionData = async () => {
    const { salesman_id, date_gte, date_lte } = JSON.parse(id);
    const filter = {
      'user.id': salesman_id,
      visited_at: { $gte: date_gte, $lte: date_lte },
    };
    const res = await MsB2b.getInvoiceCollectionExport({
      params: { filter: JSON.stringify(filter) },
    });
    const result = res?.data?.data;
    if (result) {
      setData(result);
    }
  };

  const fetchCatalogData = async () => {
    const preloadImages = async (imageUrls) => {
      const promises = imageUrls.map((url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(url);
          img.onerror = () => {
            console.warn(`Failed to load image: ${url}`);
            resolve(url);
          };
          img.src = url;
        });
      });

      await Promise.all(promises);
    };

    const brandFilter = {
      [`is_active_in_b2b${isVn() ? '_vn' : ''}`]: true,
    };
    if (id != 'all') {
      brandFilter['_id'] = id;
    }

    const brandArr = [];
    let hasMoreBrand = true;
    let skipBrand = 0;
    while (hasMoreBrand) {
      const brandsData = await MsCatalog.getBrands({
        params: {
          filter: brandFilter,
          skip: skipBrand,
          limit: BRAND_LIMIT,
        },
      });
      if (brandsData?.data?.data) {
        const brands = brandsData.data.data;

        brandArr.push(...brands);

        hasMoreBrand = brands?.length >= BRAND_LIMIT;
        skipBrand += BRAND_LIMIT;
      } else {
        hasMoreBrand = false;
        skipBrand = 0;
      }
    }

    const brandMap = {};
    if (brandArr?.length) {
      const brandIds = brandArr.map((b) => {
        brandMap[b._id.toString()] = {
          ...b,
          products: [],
        };
        return b._id;
      });

      let hasMore = true;
      let skip = 0;
      while (hasMore) {
        const allProducts = await MsCatalog.getProducts({
          params: {
            filter: {
              'brand.id': { $in: brandIds },
              [`is_active_in_b2b${isVn() ? '_vn' : ''}`]: true,
              [`combinations.is_active_in_b2b${isVn() ? '_vn' : ''}`]: true,
            },
            fields: 'images brand name combinations id default_category i18n',
            limit: PRODUCT_LIMIT,
            skip,
          },
        });

        if (allProducts?.data?.data) {
          const products = allProducts.data.data;

          const imageUrls = [];
          products.map((p) => {
            if (brandMap[p.brand.id.toString()]) {
              p.combinations = p.combinations
                .filter((c) => c[`is_active_in_b2b${isVn() ? '_vn' : ''}`])
                .map((comb) => {
                  let variant = [];
                  const coverImage = (comb.images || []).find(
                    (img) => img.is_cover == true,
                  );
                  if (coverImage) {
                    comb.combination_image = imageSource(coverImage.url);
                  } else {
                    comb.combination_image = imageSource(comb.images?.[0]?.url);
                  }
                  if (comb.attributes) {
                    const attributes = Object.entries(comb.attributes);
                    attributes.forEach((attribute) => {
                      variant.push(attribute[0] + ' - ' + attribute[1].name);
                    });
                  }
                  comb.variant = variant = variant.join(', ');

                  imageUrls.push(comb.combination_image);

                  return comb;
                });

              p.images = imageSource(
                p.images[0]?.url || p.combinations[0]?.combination_image,
              );
              if (isVn()) {
                p.name = p?.i18n?.['vi']?.name || p.name;
                if (p.default_category) {
                  p.default_category.name =
                    p?.i18n?.['vi']?.default_category?.name ||
                    p.default_category.name;
                }
              }

              imageUrls.push(p.images);

              brandMap[p.brand.id.toString()].products.push(p);
            }
          });

          // Preload image
          await preloadImages(imageUrls);

          await new Promise((r) => setTimeout(r, 500));

          hasMore = products?.length >= PRODUCT_LIMIT;
          skip += PRODUCT_LIMIT;
        } else {
          hasMore = false;
          skip = 0;
        }
      }

      const final = Object.values(brandMap);
      setData(final);
    } else {
      setData([]);
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        if (pdfType == 'customer') {
          await fetchCustomerData();
        } else if (pdfType == 'skp') {
          await fetchSkpData();
        } else if (pdfType == 'order') {
          await fetchOrderData();
        } else if (pdfType == 'catalog') {
          await fetchCatalogData();
        } else if (pdfType == 'request-product') {
          await fetchRequestProductData();
        } else if (pdfType == 'invoice-collection') {
          await fetchInvoiceCollectionData();
        }
      } catch (error) {
        console.log('🚀🚀🚀 ~ error:', error);
      }
      setIsLoading(false);
    })();
  }, [pdfType, id]);

  // Redirect if type not valid or not web platform
  useEffect(() => {
    if (!mounted) {
      return;
    }

    if (!isLoggedin) {
      dispatch(Auth.logout());
    } else {
      if (!isWebPlatform || !AVAILABLE_TYPE.includes(pdfType)) {
        const rootNavigator = props.navigation.getParent();
        if (rootNavigator) {
          rootNavigator.navigate('HomeNavigator');
        } else {
          props.navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: 'HomeNavigator' }],
            }),
          );
        }
      }
    }

    // Remove token from URL without pushing a new history entry
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('t');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }, [props.navigation, isLoggedin, mounted]);

  useEffect(() => {
    if (docElement && !isLoading && data) {
      // Generate PDF URL on mobile only
      (async () => {
        try {
          await generateBlob();
        } catch (error) {
          console.log('error: ', error);
        }
      })();
    }
  }, [docElement, isLoading, data]);

  const generateBlob = async () => {
    const instance = renderPdf(docElement);
    const blobData = await instance.toBlob();
    setBlob(blobData);
  };

  const generatePdfUrl = async () => {
    if (pdfUrl) {
      return pdfUrl;
    }
    const fileName = getFilename(pdfType, id);
    const response = await axios.get(
      `${Config.MS_SOCO_PUBLIC_API_URL}/pre-signed-url`,
      {
        params: {
          fileName: fileName,
          contentType: 'application/pdf',
          isReplaceFile: true,
        },
      },
    );
    if (response?.data?.data) {
      const arrayBuffer = await blob.arrayBuffer();
      const signUrl = response.data.data.signedUrl;

      const tempAxios = axios.create();
      await tempAxios.put(signUrl, arrayBuffer, {
        headers: { 'Content-Type': 'application/pdf' },
      });

      setPdfUrl(response.data.data.url);
      return response.data.data.url;
    }
    return null;
  };

  const handleDownload = () => {
    const fileName = getFilename(pdfType, id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSendEmail = async () => {
    try {
      const urlForEmail = await generatePdfUrl();

      await MsB2b.sendPdfEmail(pdfType, urlForEmail);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const handlePreview = async () => {
    try {
      const urlForPreview = await generatePdfUrl();

      await Linking.openURL(urlForPreview);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  return (
    <View style={styles.container}>
      {Template && !showViewer ? (
        <View style={styles.loaderWrap}>
          <Loader />
        </View>
      ) : (
        <>
          <View style={styles.btnWrapper}>
            <Button
              type="primary"
              rounded
              buttonStyle={styles.btnStyle}
              title={
                <SociollaText style={{ color: Colors.white }} type="bold">
                  Download
                </SociollaText>
              }
              onPress={handleDownload}
              disabled={!showViewer}
            />
            {isMobileWeb ? (
              <Button
                type="success"
                rounded
                buttonStyle={styles.btnStyle}
                title={
                  <SociollaText style={{ color: Colors.white }} type="bold">
                    Preview
                  </SociollaText>
                }
                onPress={handlePreview}
                disabled={!showViewer}
              />
            ) : (
              false
            )}
            <Button
              type="grey"
              rounded
              buttonStyle={styles.btnStyle}
              title={
                <SociollaText style={{ color: Colors.blue10 }} type="bold">
                  Send Email
                </SociollaText>
              }
              onPress={handleSendEmail}
              disabled={!showViewer}
            />
          </View>
          {isWeb ? (
            <View style={styles.viewerWrapper}>
              {showViewer && isWeb ? (
                <PDFViewer style={styles.pdfViewer}>{docElement}</PDFViewer>
              ) : (
                false
              )}
            </View>
          ) : (
            false
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flex: 1,
    backgroundColor: Colors.white,
    height: '100vh',
  },
  viewerWrapper: {
    flex: 1,
  },
  btnWrapper: {
    flexDirection: 'row',
    marginVertical: 10,
    justifyContent: 'space-around',
    ...(isMobileWeb
      ? {
          flex: 1,
          alignItems: 'center',
        }
      : {}),
  },
  btnStyle: {
    width: 120,
  },
  loaderWrap: {
    padding: 16,
  },
  pdfViewer: {
    flex: 1,
    width: '100%',
    height: '100vh',
  },
});

export default PdfViewerScreen;
